import * as React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl";
import MonacoEditor from 'react-monaco-editor';
import { Link } from 'react-router-dom';
import { preferredEditor } from 'src/constants/profileSettings';
import { Log, logAction, logType } from 'src/utils/api';
import { shortenTo } from 'src/utils/stringManipulation';
import GeneratorEntity from './../../../models/Generator';
import { history } from './../../../store/store';
import translations from './translations';

interface IProps extends InjectedIntlProps {
    content: string
    errorMessage: string
    generator: GeneratorEntity
    id: string
    isLoadingValidation: boolean
    isYaml: boolean
    isShowingRunWindow: boolean
    theme: string
    receivedUpdate: boolean
    syntaxError: boolean
    validated: boolean
    validationError: boolean

    changeLanguage: () => void
    contentChanged: (value: string) => void
    refresh: () => void
    toggleRunView: () => void
    runGenerator: (code: string) => void
    saveGenerator: () => void
    setGen: () => void
    switchView: (view: string) => void
    validateGenerator: () => void
    download: () => void
}

interface IState {
    appendResult: boolean,
    generatorResult: string,
    isLoadingRunning: string,
    isLoadingSaving: string,
    newID: string
}
class CodeEditorComponent extends React.Component<IProps, IState> {
    public constructor(props: IProps) {
        super(props);
        this.state = {
            appendResult: false,
            generatorResult: '',
            isLoadingRunning: '',
            isLoadingSaving: '',
            newID: ''
        };
    }

    public componentDidMount() {
        if ((this.props.generator.id === "new" && this.props.receivedUpdate === false)) {
            this.props.setGen()
        }
    }

    public componentDidUpdate() {
        if (this.props.generator.hasAllData === true && this.props.receivedUpdate === false) {
            this.props.setGen()
        }
    }

    public componentWillUnmount() {
        // window.console.info("unmounting code");
    }

    public editorDidMount(editor: any, monaco: any) {
        editor.focus();
    }

    public onChange(newValue: any, e: any) {
        this.props.contentChanged(newValue)
    }

    public goBack = () => {
        history.push("/generators")
    }

    public goToFormsEditor = () => {
        this.props.switchView(preferredEditor.formEditor)
        Log(logType.action, logAction.clickHandler, "CodeGeneratorComponent", "Pressed the changed to Form Editor button!");
    }

    public handleKeyDown = (e: any) => {
        const charCode: string = String.fromCharCode(e.which).toLowerCase();
        if (e.ctrlKey && charCode === 's' || e.metaKey && charCode === 's') {
            e.preventDefault();
            this.props.saveGenerator();
        }
    }

    public render() {
        this.onChange = this.onChange.bind(this)
        const options: any = {
            autoIndent: true,
            automaticLayout: true,
            extraEditorClassName: 'generator-editor',
            minimap: {
                showSlider: 'always'
            },
            scrollbar: {
                horizontal: 'hidden',
                vertical: 'hidden'
            },
            selectOnLineNumbers: true,
            theme: this.props.theme,

        };

        return (
            <>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <nav className="breadcrumb is-fixed-top">
                                <ul>
                                    <li>
                                        <Link to="/generators"><FormattedMessage {...translations.generators} /></Link>
                                    </li>@
                                    {this.props.id === "new"
                                        ? (
                                            <li className="is-active">
                                                <a><FormattedMessage {...translations.newgenerator} /></a>
                                            </li>
                                        )
                                        : (
                                            <li className="is-active">
                                                <a>{shortenTo(this.props.generator.name, 50)}</a>
                                            </li>
                                        )}
                                </ul>
                            </nav>
                        </div>
                    </div>
                    <div className="level-right">
                        <div className="level-item">
                            <button className="button" onClick={this.props.download}>
                                <span className="icon is-small">
                                    <i className="fas fa-download" />
                                </span>
                                <span> Download</span>
                            </button>
                        </div>
                        <div className="level-item">
                            <div className="field has-addons">
                                <p className="control">
                                    <button className={"button" + (this.props.isYaml ? " is-info" : '')} disabled={this.props.syntaxError} onClick={this.props.changeLanguage}>
                                        <span className="icon is-small">
                                            <i className="fal fa-code" />
                                        </span>
                                        <span>YAML</span>
                                    </button>
                                </p>
                                <p className="control">
                                    <button className={"button" + (!this.props.isYaml ? " is-info" : '')} disabled={this.props.syntaxError} onClick={this.props.changeLanguage}>
                                        <span className="icon is-small">
                                            <i className="fal fa-code" />
                                        </span>
                                        <span> JSON</span>
                                    </button>
                                </p>
                            </div>
                        </div>
                        <div className="level-item">
                            <button className="button" disabled={!this.props.validated} onClick={this.goToFormsEditor}>
                                {this.props.intl.formatMessage(translations.switchToForm)}
                            </button>
                        </div>
                    </div>

                </nav>


                <div className="box" onKeyDown={(e) => this.handleKeyDown(e)} tabIndex={0}>  { /* tslint:disable-line */}
                    <MonacoEditor
                        width="100%"
                        height="600"
                        language="yaml"
                        value={this.props.content}
                        options={options}
                        onChange={this.onChange}
                        editorDidMount={this.editorDidMount}
                    />
                    {this.props.syntaxError || this.props.validationError
                        ?
                        <article className="message is-danger">
                            <div className="message-body" dangerouslySetInnerHTML={{ __html: this.props.errorMessage }} />
                        </article>
                        : ''}

                </div>

                <hr />

                <nav className="level">
                    {/* Run & Validate */}
                    <div className="level-left">
                        <div className="level-item">
                            <button className="button is-dark is-outlined" onClick={this.props.toggleRunView}>
                                <span className="icon">
                                    <i className="far fa-play-circle" />
                                </span>
                                <span>{this.props.isShowingRunWindow ?
                                    this.props.intl.formatMessage(translations.hideRunWindow) :
                                    this.props.intl.formatMessage(translations.showRunWindow)}
                                </span>
                            </button>
                        </div>
                        <div className="level-item">
                            <button className={"button is-dark is-outlined " + (this.props.isLoadingValidation ? 'is-loading' : '')} disabled={this.props.syntaxError} onClick={this.props.validateGenerator}>
                                <span className="icon">
                                    <i className="far fa-check-circle" />
                                </span>
                                <span>{this.props.intl.formatMessage(translations.validate)}</span>
                            </button>
                        </div>
                        {this.props.isShowingRunWindow &&
                            <div className="level-item">
                                <p className="buttons">
                                    <button className="button is-warning is-outlined" onClick={this.goBack} >
                                        <span className="icon is-small">
                                            <i className="far fa-times-circle" />
                                        </span>
                                        <span>{this.props.intl.formatMessage(translations.exit)}</span>
                                    </button>
                                    <button className={"button is-success " + this.state.isLoadingSaving} onClick={this.props.saveGenerator}>
                                        <span className="icon is-small">
                                            <i className="fas fa-check-double" />
                                        </span>
                                        <span>{this.props.intl.formatMessage(translations.save)}</span>
                                    </button>
                                </p>
                            </div>
                        }
                    </div>
                    {/* Cancel & Save */}
                    <div className="level-right">
                        <div className="level-item">
                            <p className="buttons">
                                <button className="button is-warning is-outlined" onClick={this.goBack} >
                                    <span className="icon is-small">
                                        <i className="far fa-times-circle" />
                                    </span>
                                    <span>{this.props.intl.formatMessage(translations.exit)}</span>
                                </button>
                                <button className={"button is-success " + this.state.isLoadingSaving} onClick={this.props.saveGenerator}>
                                    <span className="icon is-small">
                                        <i className="fas fa-check-double" />
                                    </span>
                                    <span>{this.props.intl.formatMessage(translations.save)}</span>
                                </button>
                            </p>
                        </div>
                    </div>
                </nav>
            </>
        )
    }
}

export default injectIntl(CodeEditorComponent)



// WEBPACK FOOTER //
// ./src/Containers/generators/EditGenerator/CodeEditorComponent.tsx